import Dexie, { Table } from 'dexie';
// Add a utility function to generate random unique IDs
function generateUniqueId(): number {
    // Generate a random number between 100000 and 999999999
    return Math.floor(100000 + Math.random() * 999899999);
}
// Metadata interface for tracking online/offline status
interface EntityMetadata {
    // isNew: boolean;
    // onlineId?: number;
    // lastSynced?: string;
    // status: 'new' | 'modified' | 'synced';
    isOnline?: boolean
    onlineId?: number
}

// Base interfaces with metadata
// interface BaseEntity {
//     id?: number;
//     metadata?: EntityMetadata;
// }


// Define interfaces matching backend models
export interface Project extends EntityMetadata {
    id?: number;
    projectName: string;
    projectDescription: string;
    createdBy: string;
    target?: number;
    percentage?: number;
    plusByUser?: number;
    overallDecision?: 'pass' | 'fail' | 'pending';
    createdAt: string;
    updatedAt: string;
    reasonForInvalidStackingTest?: string;
    validStackingTest?: boolean;
    overallDecisionFailureNote?: string;
    offlineMetadata?: {
        downloadedAt: string;
        onlineId: number;
        status: 'downloaded' | 'synced';
        syncStatus: 'pending' | 'syncing' | 'completed';
    };
}

export interface Pallet extends EntityMetadata {
    id?: number;
    projectId: number;
    variant: string;
    name: string;
    mode?: number;
    numberOfPallets: number;
    percentage?: number;
    plusByUser?: number;
    stackTestLayoutWeek?: string;
    stackPattern?: string;
    createdAt: string;
    updatedAt: string;
}

export interface ShippingMode extends EntityMetadata {
    id?: number,
    mode: number;
    transitMode: string;
    startCity: string;
    startStateName: string;
    startZip: string;
    endCity: string;
    endStateName: string;
    endZip: string;
    distanceTravelled: string;
    intermediateStepNotes?: string;
    elevationChanges: string;
    elevationMax: string;
    intermediateStopNotes?: string
    projectId?: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface PalletShippingMode extends EntityMetadata {
    id?: number,
    palletId: number;
    shippingModeId: number;
}

export interface PrimaryPackagingDetails extends EntityMetadata {
    id?: number;
    palletId: number;
    packageType: string;
    category?: string;
    brand?: string;
    application?: string;
    finishType?: string;
    packageMaterial?: string;
    filledBottleTopLoad?: number;
    diameter?: number;
    diameterUnit?: string;
    packageHeight?: number;
    packageHeightUnit?: string;
    netContent?: number;
    netContentUnit?: string;
    filledWeight?: number;
    filledWeightUnit?: string;
    bottleDesign?: string;
    canDesign?: string;
}

export interface SecondaryPackagingDetails extends EntityMetadata {
    id?: number;
    palletId: number;
    secondaryPkg: boolean;
    secondaryPackageType?: string;
    secondaryPackageCase?: string;
    secondaryUnitNetWeight?: number;
    secondaryUnitNetWeightUnit?: string;
    secondaryUnitGrossWeight?: number;
    secondaryUnitGrossWeightUnit?: string;
    secondaryCaseLength?: number;
    secondaryCaseLengthUnit?: string;
    secondaryCaseWidth?: number;
    secondaryCaseWidthUnit?: string;
    secondaryCaseHeight?: number;
    secondaryCaseHeightUnit?: string;
    filledCaseCompression?: number;
}

export interface TertiaryPackagingDetails extends EntityMetadata {
    id?: number;
    palletId: number;
    tertiaryPkg: boolean;
    tertiaryPackageType?: string;
    tertiarySecondaryPackagesPerUnit?: string;
    tertiaryUnitNetWeight?: number;
    tertiaryUnitNetWeightUnit?: string;
    tertiaryUnitGrossWeight?: number;
    tertiaryUnitGrossWeightUnit?: string;
    tertiaryCaseLength?: number;
    tertiaryCaseLengthUnit?: string;
    tertiaryCaseWidth?: number;
    tertiaryCaseWidthUnit?: string;
    tertiaryCaseHeight?: number;
    tertiaryCaseHeightUnit?: string;
}

export interface PalletDetail extends EntityMetadata {
    id?: number;
    palletId: number;
    palletTypes?: string;
}

export interface UnitLoadDetail extends EntityMetadata {
    id?: number;
    palletId: number;
    unitLoadLength?: number;
    unitLoadLengthUnit?: string;
    unitLoadWidth?: number;
    unitLoadWidthUnit?: string;
    unitLoadHeight?: number;
    unitLoadHeightUnit?: string;
    unitLoadWeight?: number;
    unitLoadWeightUnit?: string;
    unitCasesPerLayer?: number;
    unitLayersPerUnitLoad?: number;
    machineAssembled?: string;
    plant?: string;
    plantLine?: string;
    newPlantLine?: string;
    engHelp?: string;
    varietyPack?: string;
    totalCasesPerUnitLoad?: number;
    itemsPerPallet?: number;
    palletPatternDesign?: string;
}

export interface PalletizingRecipe extends EntityMetadata {
    id?: number;
    palletId: number;
    stretchGa?: string;
    stretchWidth?: number;
    stretchWidthUnit?: string;
    numberOfRevs?: number;
    cutAndWeigh?: string;
    preStretch?: number;
    stretchWrapSpecNumber?: string;
    numberOfRevsTop?: number;
    numberOfRevsMiddle?: number;
    numberOfRevsBottom?: number;
}

export interface PalletLpnNumber extends EntityMetadata {
    id?: number;
    lpnNo: string;
    endStatusPallet: string;
    rqlState: 'yes' | 'no';
    palletId: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface Lean extends EntityMetadata {
    id?: number;
    palletLpnNumberId: number;
    variant: 'preShip' | 'postShip' | 'postStack';
    weekType?: string;
    sideABottom: string;
    sideATop: string;
    sideBBottom: string;
    sideBTop: string;
    sideCBottom: string;
    sideCTop: string;
    sideDBottom: string;
    sideDTop: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface UnitLoadHeight extends EntityMetadata {
    id?: number;
    palletLpnNumberId: number;
    variant: 'preShip' | 'postShip' | 'postStack';
    weekType?: string;
    heightAB: number;
    heightBC: number;
    heightCD: number;
    heightDA: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface UnitLoadPosition extends EntityMetadata {
    id?: number;
    palletLpnNumberId: number;
    variant: 'preShip' | 'postShip' | 'postStack';
    weekType?: string;
    position: string;
    notes?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface RQL extends EntityMetadata {
    id?: number;
    palletId: number;
    variant: 'preShip' | 'postShip' | 'postStack';
    targetSampleSize: string;
    actualSampleSize: string;
    dateOfInspection: string;
    packageType: string;
    inspectedBy: string;
    totalDefects: number;
    percentDefects: number;
    passFail: string;
    unclassifiedDefects: string;
    rqlNotes: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface RQLDefect extends EntityMetadata {
    id?: number;
    rqlId: number;
    defectKeyId: number;
    defectType: 'critical' | 'major1' | 'major2' | 'minor';
    defectPercentage: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface DefectKey extends EntityMetadata {
    id?: number;
    name: string;
    defectType: 'critical' | 'major1' | 'major2' | 'minor';
    packageType: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface FTLRow extends EntityMetadata {
    id?: number;
    palletLpnNumberId: number;
    variant: 'preShip' | 'postShip';
    ftlState: 'yes' | 'no';
    topMeasured?: number;
    middleMeasured?: number;
    bottomMeasured?: number;
    notes?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface PostShipTruckTestLayout extends EntityMetadata {
    id?: number;
    shippingModeId: number;
    pickupAddress?: string;
    deliveryAddress?: string;
    pointOfContact?: string;
    pickupName?: string;
    deliveryName?: string;
    pickupPhone?: number;
    deliveryPhone?: number;
    pickupEmail?: string;
    deliveryEmail?: string;
    startingMileage?: number;
    endingMileage?: number;
    totalMilesDriven?: number;
    suspensionModel?: string;
    truckCondition?: string;
    year?: number;
    airbags?: string;
    lockBarsStraps?: string;
    truckLayoutFile?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface PostStackLayoutTest extends EntityMetadata {
    id?: number;
    projectId: number;
    palletId: number;
    stackHoldDuration?: string;
    stackPattern?: string;
    palletsUsedForStackTesting?: number;
    recommendedMinimumPalletsForStackTesting?: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface NewFailureMode extends EntityMetadata {
    id?: number;
    projectId: number;
    failureModeName: string;
    status: 'pass' | 'fail' | 'dataMissing';
    createdAt?: string;
    updatedAt?: string;
}

export interface EditSession extends EntityMetadata {
    id?: number;
    stage: string;  // 'preShip', 'postShip', 'postStack'
    component: string;  // 'lean', 'ftl', 'unitLoadPosition', etc.
    lastActivity: string;
    expiresAt: string;
    projectId: number;
    userId: number;
    palletId: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface TruckLayout extends EntityMetadata {
    id?: number;
    shippingModeId: number;
    pickupName: string;
    deliveryName: string;
    pickupPhone: number;
    deliveryPhone: number;
    pickupEmail: string;
    deliveryEmail: string;
    startingMileage: number;
    endingMileage: number;
    totalMilesDriven: number;
    suspensionModel: string;
    truckCondition: string;
    year: number;
    airbags: string;
    lockBarsStraps: string;
    truckLayout?: File | null;
    createdAt?: string;
    updatedAt?: string;
}

export class OfflineDatabase extends Dexie {
    projects!: Table<Project>;
    pallets!: Table<Pallet>;
    shippingModes!: Table<ShippingMode>;
    palletShippingModes!: Table<PalletShippingMode>;
    primaryPackagingDetails!: Table<PrimaryPackagingDetails>;
    secondaryPackagingDetails!: Table<SecondaryPackagingDetails>;
    tertiaryPackagingDetails!: Table<TertiaryPackagingDetails>;
    palletDetails!: Table<PalletDetail>;
    unitLoadDetails!: Table<UnitLoadDetail>;
    palletizingRecipes!: Table<PalletizingRecipe>;
    palletLpnNumbers!: Table<PalletLpnNumber>;
    leans!: Table<Lean>;
    unitLoadHeights!: Table<UnitLoadHeight>;
    unitLoadPositions!: Table<UnitLoadPosition>;
    rqls!: Table<RQL>;
    rqlDefects!: Table<RQLDefect>;
    defectKeys!: Table<DefectKey>;
    ftlRows!: Table<FTLRow>;
    postShipTruckTestLayouts!: Table<PostShipTruckTestLayout>;
    postStackLayoutTests!: Table<PostStackLayoutTest>;
    newFailureModes!: Table<NewFailureMode>;
    editSessions!: Table<EditSession>;
    truckLayouts!: Table<TruckLayout>;

    constructor() {
        super('offlineDatabase_v2');
        this.version(2).stores({
            projects: '++id, projectName',
            pallets: '++id, projectId',
            shippingModes: '++id, projectId',
            palletShippingModes: '++id, palletId, shippingModeId',
            primaryPackagingDetails: '++id, palletId',
            secondaryPackagingDetails: '++id, palletId',
            tertiaryPackagingDetails: '++id, palletId',
            palletDetails: '++id, palletId',
            unitLoadDetails: '++id, palletId',
            palletizingRecipes: '++id, palletId',
            palletLpnNumbers: '++id, palletId',
            leans: '++id, palletLpnNumberId, variant, [palletLpnNumberId+variant]',
            unitLoadHeights: '++id, palletLpnNumberId, variant, [palletLpnNumberId+variant]',
            unitLoadPositions: '++id, palletLpnNumberId, variant, [palletLpnNumberId+variant]',
            rqls: '++id, palletId, variant, [palletId+variant]',
            rqlDefects: '++id, rqlId, defectKeyId, defectType',
            defectKeys: '++id, name, defectType, packageType',
            ftlRows: '++id, palletLpnNumberId, variant, [palletLpnNumberId+variant]',
            postShipTruckTestLayouts: '++id, shippingModeId',
            postStackLayoutTests: '++id, projectId, palletId',
            newFailureModes: '++id, projectId',
            editSessions: '++id, projectId, palletId, userId',
            truckLayouts: '++id, shippingModeId',
        });

    }

    // Generate pallet like backend's generatePallet
    async generatePallet(data: { type: 'create' | 'update', palletId?: number, palletDetails: any, shippingModeIds: number[] }): Promise<any> {
        return await this.transaction('rw', [this.pallets, this.projects, this.shippingModes, this.palletShippingModes], async () => {
            let pallet;
            const timestamp = new Date().toISOString();

            if (data.type === 'create') {
                console.log(data, 'finall')
                // Get project's percentage and plusByUser
                const project = await this.projects.get(data.palletDetails.projectId);
                if (!project) throw new Error('Project not found');

                // Create pallet
                const palletId = await this.pallets.add({
                    ...data.palletDetails,
                    plusByUser: project.plusByUser,
                    percentage: project.percentage,
                    createdAt: timestamp,
                    updatedAt: timestamp
                });

                pallet = await this.pallets.get(palletId);
                console.log(pallet, 'palletoo', palletId, 'palletId')
                // Add shipping modes
                console.log(data.shippingModeIds, 'killed')
                if (data.shippingModeIds?.length > 0) {
                    console.log(data.shippingModeIds, 'data.shippingModeIds if ran')
                    await Promise.all(data.shippingModeIds.map(shippingModeId =>
                        this.palletShippingModes.add({
                            palletId: palletId,
                            shippingModeId
                        })
                    ));
                }
            } else if (data.type === 'update' && data.palletId) {
                await this.pallets.update(data.palletId, {
                    ...data.palletDetails,
                    updatedAt: timestamp
                });

                // Update shipping modes
                await this.palletShippingModes.where('palletId').equals(data.palletId).delete();
                if (data.shippingModeIds?.length > 0) {
                    await Promise.all(data.shippingModeIds.map(shippingModeId =>
                        this.palletShippingModes.add({
                            palletId: data.palletId!,
                            shippingModeId
                        })
                    ));
                }

                pallet = await this.pallets.get(data.palletId);
            }

            if (!pallet) throw new Error('Failed to create/update pallet');

            // Get shipping modes
            const palletShippingModes = await this.palletShippingModes
                .where('palletId')
                .equals(pallet.id!)
                .toArray();

            const shippingModes = await Promise.all(
                palletShippingModes.map(psm => this.shippingModes.get(psm.shippingModeId))
            );

            return {
                ...pallet,
                shippingModes: shippingModes.filter(sm => sm !== undefined)
            };
        });
    }

    // Get all pallets for project like backend's getAllPalletsForProject
    async getAllPalletsForProject(projectId: number): Promise<{ pallets: Pallet[] }> {
        console.log(projectId, 'projectId')
        // return await this.transaction('r', [this.pallets, this.shippingModes, this.palletShippingModes], async () => {
        try {
            // Check if project exists first
            const test = await this.palletShippingModes.toArray()
            console.log(test, 'testtttt')
            const project = await this.projects.get(projectId);
            if (!project) {
                console.log('Project not found:', projectId);
                return { pallets: [] };
            }

            // Get basic pallet data
            const pallets = await this.pallets
                .where('projectId')
                .equals(projectId)
                .toArray();

            // If no pallets found, return empty array
            if (!pallets || pallets.length === 0) {
                console.log('No pallets found for project:', projectId);
                return { pallets: [] };
            }

            // Get shipping modes for each pallet
            const palletsWithShippingModes = await Promise.all(
                pallets.map(async pallet => {
                    const palletShippingModes = await this.palletShippingModes
                        .where('palletId')
                        .equals(pallet.id!)
                        .toArray();

                    const shippingModes = await Promise.all(
                        palletShippingModes.map(async psm => {
                            const shippingMode = await this.shippingModes.get(psm.shippingModeId);
                            return shippingMode ? { ...psm, shippingMode } : undefined;

                        })
                    );
                    console.log(shippingModes, 'shippingModes inside new file')
                    return {
                        ...pallet,
                        shippingModes: shippingModes.filter(sm => sm !== undefined)
                    };
                })
            );

            return { pallets: palletsWithShippingModes };
        } catch (error) {
            console.error('Error in getAllPalletsForProject:', error);
            return { pallets: [] }; // Return empty array instead of throwing
        }
        // });
    }

    // Create or update pallet details like backend's createPalletDetails
    async createPalletDetails(data: {
        type: string;
        palletId: number;
        primaryPackagingDetails?: any;
        secondaryPackagingDetails?: any;
        tertiaryPackagingDetails?: any;
        palletDetails?: any;
        unitLoadDetails?: any;
        palletizingRecipe?: any;
    }): Promise<void> {
        const updateOrCreate = async (table: Table, details: any, palletId: number) => {
            if (details) {
                const existing = await table
                    .where('palletId')
                    .equals(palletId)
                    .first();

                const timestamp = new Date().toISOString();
                if (existing) {
                    await table.update(existing.id!, {
                        ...details,
                        updatedAt: timestamp
                    });
                } else {
                    await table.add({
                        ...details,
                        palletId,
                        createdAt: timestamp,
                        updatedAt: timestamp
                    });
                }
            }
        };

        await this.transaction('rw', [
            this.primaryPackagingDetails,
            this.secondaryPackagingDetails,
            this.tertiaryPackagingDetails,
            this.palletDetails,
            this.unitLoadDetails,
            this.palletizingRecipes
        ], async () => {
            await Promise.all([
                updateOrCreate(this.primaryPackagingDetails, data.primaryPackagingDetails, data.palletId),
                updateOrCreate(this.secondaryPackagingDetails, data.secondaryPackagingDetails, data.palletId),
                updateOrCreate(this.tertiaryPackagingDetails, data.tertiaryPackagingDetails, data.palletId),
                updateOrCreate(this.palletDetails, data.palletDetails, data.palletId),
                updateOrCreate(this.unitLoadDetails, data.unitLoadDetails, data.palletId),
                updateOrCreate(this.palletizingRecipes, data.palletizingRecipe, data.palletId)
            ]);
        });
    }

    // Get pallet details like backend's getPalletDetails
    async getPalletDetails(palletId: number): Promise<any> {
        return await this.transaction('r', [
            this.pallets,
            this.primaryPackagingDetails,
            this.secondaryPackagingDetails,
            this.tertiaryPackagingDetails,
            this.palletDetails,
            this.unitLoadDetails,
            this.palletizingRecipes,
            this.shippingModes,
            this.palletShippingModes
        ], async () => {
            const pallet = await this.pallets.get(palletId);
            if (!pallet) return undefined;

            // Get all related details
            const [
                primaryPackaging,
                secondaryPackaging,
                tertiaryPackaging,
                palletDetail,
                unitLoadDetail,
                palletizingRecipe,
                palletShippingModes
            ] = await Promise.all([
                this.primaryPackagingDetails.where('palletId').equals(palletId).first(),
                this.secondaryPackagingDetails.where('palletId').equals(palletId).first(),
                this.tertiaryPackagingDetails.where('palletId').equals(palletId).first(),
                this.palletDetails.where('palletId').equals(palletId).first(),
                this.unitLoadDetails.where('palletId').equals(palletId).first(),
                this.palletizingRecipes.where('palletId').equals(palletId).first(),
                this.palletShippingModes.where('palletId').equals(palletId).toArray()
            ]);
            console.log(palletizingRecipe, 'palletizingRecipe in  db')
            // Get shipping modes
            const shippingModes = await Promise.all(
                palletShippingModes.map(psm => this.shippingModes.get(psm.shippingModeId))
            );

            return {
                ...pallet,
                primaryPackagingDetails: [primaryPackaging],
                secondaryPackagingDetails: [secondaryPackaging],
                tertiaryPackagingDetails: [tertiaryPackaging],
                palletDetails: [palletDetail],
                unitLoadDetails: [unitLoadDetail],
                palletizingRecipe: [palletizingRecipe],
                shippingModes: shippingModes.filter(sm => sm !== undefined)
            };
        });
    }

    // Get project with all related data
    async getProject(id: number): Promise<any> {
        return await this.transaction('r', [this.projects, this.shippingModes], async () => {
            const project = await this.projects.get(id);
            if (!project) return undefined;

            const shippingModes = await this.shippingModes
                .where('projectId')
                .equals(id)
                .toArray();

            return {
                ...project,
                projectDetails: {
                    projectName: project.projectName,
                    projectDescription: project.projectDescription
                },
                shippingModes
            };
        });
    }

    // Get all projects with basic details
    async getAllProjects(): Promise<any[]> {
        try {
            const projects = await this.projects.toArray();

            return projects.map(project => ({
                id: project.id,
                projectName: project.projectName,
                projectDescription: project.projectDescription,
                createdBy: project.createdBy,
                createdAt: project.createdAt,
                updatedAt: project.updatedAt,
                status: project.offlineMetadata ? 'Offline' : 'Online',
                // Include any other basic fields needed for the table display
                target: project.target,
                offlineMetadata: project?.offlineMetadata,
                percentage: project.percentage,
                plusByUser: project.plusByUser,
                overallDecision: project.overallDecision
            }));
        } catch (error) {
            console.error('Error getting projects:', error);
            return []; // Return empty array if there's an error
        }
    }
}

async function deleteOldDatabase() {
    try {
        const databases = await window.indexedDB.databases();
        const oldDbs = databases.filter(db => db.name === 'offlineDb' || db.name === 'offlineDatabase');
        for (const db of oldDbs) {
            if (db.name) {
                await window.indexedDB.deleteDatabase(db.name);
            }
        }
    } catch (error) {
        console.log('Error cleaning up old databases:', error);
    }
}

// await deleteOldDatabase();
export const db = new OfflineDatabase();
